/* eslint-disable consistent-return, default-case, no-param-reassign */
/*
 *
 * Autopay reducer
 *
 */

import produce from 'immer';
import isEmpty from 'lodash/isEmpty';

import { RESET_SWITCH_LOAN_STATE, RESET_USER_STATE } from '../../Main/constants';
import {
  AUTOPAY_ENABLE,
  AUTOPAY_ENABLE_FAILURE,
  AUTOPAY_ENABLE_SUCCESS,
  AUTOPAY_ENABLE_VALIDATION_FAILURE,
  CANCEL,
  CANCEL_FAILURE,
  CANCEL_SUCCESS,
  CHANGE,
  CHANGE_FAILURE,
  CHANGE_INPUT,
  CHANGE_STEP,
  CHANGE_SUCCESS,
  CHANGE_VALIDATION_FAILURE,
  FETCH_STATUS,
  FETCH_STATUS_FAILURE,
  FETCH_STATUS_SUCCESS,
  RESET_AUTOPAY_STATE,
  RESET_CANCEL_CONFIRMATION,
  RESET_CONFIRMATION,
  RESET_REQUEST_STATUS,
  SET_IS_AMOUNT_DRAWER_OPEN,
  SET_IS_IN_AUTOPAY_FLOW,
  SET_IS_MODAL_OPEN,
  STOP,
  STOP_FAILURE,
  STOP_SUCCESS,
  UPDATE_AMOUNT,
  UPDATE_EMAIL_ALERT_SETTING_FAILURE,
  UPDATE_FORM_ERROR,
} from './constants';

export const initialState = {
  cancelConfirmation: {},
  confirmationNumber: null,
  confirmationType: null,
  desktopView: 'changeOrEnable',
  enableRequestData: null,
  errors: {
    bannerError: false,
    serviceError: {
      cancel: false,
      change: false,
      enable: false,
      fetchStatus: false,
      stop: false,
      updateEmailAlerts: false,
    },
  },
  form: {
    account: { beginDate: null, delayDays: null },
    alerts: { receivesPaymentEmails: null },
    amount: { additionalPrincipal: null },
  },
  formErrors: {
    account: { beginDate: '', delayDays: '' },
    amount: { additionalPrincipal: '' },
  },
  isAmountDrawerOpen: false,
  isInAutopayFlow: false,
  isModalOpen: { cancel: false, change: false, stop: false },
  prevConfirmationType: null,
  status: {
    cancel: 'idle',
    change: 'idle',
    enable: 'idle',
    fetchStatus: 'idle',
    stop: 'idle',
  },
  statusData: {
    allowedOperation: null,
    allowedSetting: null,
    currentSetting: null,
    isLoanMancodeY: false,
    regularMonthlyPayment: null,
    status: null,
  },
  step: 1,
};

const autopayReducer = produce((draft, { payload, type }) => {
  switch (type) {
    case AUTOPAY_ENABLE: {
      draft.confirmationType = 'enable';
      draft.errors = initialState.errors;
      draft.status.enable = 'loading';
      break;
    }
    case AUTOPAY_ENABLE_FAILURE: {
      draft.errors.serviceError.enable = true;
      draft.status.enable = 'failed';
      break;
    }
    case AUTOPAY_ENABLE_SUCCESS: {
      const { confirmationNumber, ...restPayload } = payload;
      draft.confirmationNumber = confirmationNumber;
      draft.enableRequestData = restPayload;
      draft.status.enable = 'succeeded';
      draft.status.fetchStatus = 'idle';
      break;
    }
    case AUTOPAY_ENABLE_VALIDATION_FAILURE: {
      const { account, amount, bannerError } = payload;
      draft.errors.bannerError = bannerError;
      Object.keys(account).forEach((key) => {
        draft.formErrors.account[key] = account[key];
      });
      Object.keys(amount).forEach((key) => {
        draft.formErrors.amount[key] = amount[key];
      });
      const accountErrors = Object.values(draft.formErrors.account).reduce((acc, value) => acc
      || !!value);
      const amountErrors = Object.values(draft.formErrors.amount).reduce((acc, value) => acc
        || !!value);
      if (accountErrors) {
        draft.isAccountDrawerOpen = true;
      } else if (amountErrors) {
        draft.isAmountDrawerOpen = true;
      }
      draft.status.enable = 'failed';
      break;
    }
    case CANCEL: {
      draft.cancelConfirmation = {
        amount: draft.statusData.currentSetting.totalMonthlyPayment,
        date: draft.statusData.currentSetting.beginDate,
        status: draft.statusData.status,
      };
      draft.prevConfirmationType = draft.confirmationType;
      draft.confirmationType = 'cancel';
      draft.errors = initialState.errors;
      draft.status.cancel = 'loading';
      break;
    }
    case CANCEL_FAILURE: {
      draft.errors.serviceError.cancel = true;
      draft.status.cancel = 'failed';
      // for desktop
      draft.step = 1;
      draft.desktopView = 'cancel';
      draft.prevConfirmationType = initialState.prevConfirmationType;
      break;
    }
    case CANCEL_SUCCESS: {
      const { cancelConfirmationNumber } = payload;
      draft.cancelConfirmation.confirmationNumber = cancelConfirmationNumber;
      draft.errors = initialState.errors;
      draft.status.fetchStatus = 'idle';
      draft.status.cancel = 'succeeded';
      // for desktop
      draft.step = 1;
      draft.desktopView = 'cancel';
      draft.prevConfirmationType = initialState.prevConfirmationType;
      break;
    }
    case CHANGE: {
      draft.confirmationType = 'change';
      draft.errors = initialState.errors;
      draft.status.change = 'loading';
      break;
    }
    case CHANGE_FAILURE: {
      draft.errors.serviceError.change = true;
      draft.status.change = 'failed';
      break;
    }
    case CHANGE_SUCCESS: {
      const { confirmationNumber } = payload;
      draft.confirmationNumber = confirmationNumber;
      draft.status.fetchStatus = 'idle';
      draft.status.change = 'succeeded';
      break;
    }
    case CHANGE_VALIDATION_FAILURE: {
      const { account, amount, bannerError } = payload;
      draft.errors.bannerError = bannerError;
      Object.keys(account).forEach((key) => {
        draft.formErrors.account[key] = account[key];
      });
      Object.keys(amount).forEach((key) => {
        draft.formErrors.amount[key] = amount[key];
      });
      draft.status.change = 'failed';
      break;
    }
    case CHANGE_INPUT: {
      const { field, form, value } = payload;
      draft.form[form][field] = value;
      if (draft.formErrors[form] && draft.formErrors[form][field]) {
        draft.formErrors[form][field] = '';
      }
      break;
    }
    case CHANGE_STEP: {
      const { step } = payload;
      draft.step = step;
      break;
    }
    case FETCH_STATUS: {
      draft.errors.serviceError.fetchStatus = initialState.errors.serviceError.fetchStatus;
      draft.status.fetchStatus = 'loading';
      break;
    }
    case FETCH_STATUS_FAILURE: {
      const { error } = payload;
      draft.status.fetchStatus = 'failed';
      draft.errors.serviceError.fetchStatus = error;
      break;
    }
    case FETCH_STATUS_SUCCESS: {
      const {
        allowedOperation,
        allowedSetting,
        currentSetting,
        isLoanMancodeY,
        regularMonthlyPayment,
        status,
      } = payload;
      if (currentSetting && !isEmpty(draft.cancelConfirmation)) {
        const { beginDate, cancellableDate, totalMonthlyPayment } = currentSetting;
        draft.cancelConfirmation.amount = totalMonthlyPayment;
        draft.cancelConfirmation.date = beginDate || cancellableDate;
      }
      draft.status.fetchStatus = 'succeeded';

      if (allowedOperation === 'Request_Change_Or_Stop' || allowedOperation === 'Request_Enable') {
        draft.isInAutopayFlow = true;
      }

      draft.statusData = {
        allowedOperation,
        allowedSetting,
        currentSetting,
        isLoanMancodeY,
        regularMonthlyPayment,
        status,
      };
      break;
    }
    case RESET_AUTOPAY_STATE: {
      draft.errors.bannerError = initialState.errors.bannerError;
      draft.formErrors = initialState.formErrors;
      draft.form = {
        account: initialState.form.account,
        alerts: initialState.form.alerts,
        amount: initialState.form.amount,
      };
      draft.status = initialState.status;
      draft.step = initialState.step;
      break;
    }
    case RESET_CANCEL_CONFIRMATION: {
      draft.confirmationType = initialState.confirmationType;
      draft.desktopView = 'changeOrEnable';
      draft.errors.serviceError.cancel = false;
      draft.status.cancel = 'idle';
      break;
    }
    case RESET_CONFIRMATION: {
      draft.enableRequestData = initialState.enableRequestData;
      draft.confirmationNumber = initialState.confirmationNumber;
      draft.errors.serviceError = initialState.errors.serviceError;
      draft.status.enable = 'idle';
      draft.status.change = 'idle';
      break;
    }
    case RESET_REQUEST_STATUS: {
      const { statusType } = payload;
      draft.status[statusType] = 'idle';
      break;
    }
    case RESET_SWITCH_LOAN_STATE: {
      return initialState;
    }
    case RESET_USER_STATE: {
      return initialState;
    }
    case SET_IS_AMOUNT_DRAWER_OPEN: {
      const { value } = payload;
      draft.isAmountDrawerOpen = value;
      break;
    }
    case SET_IS_IN_AUTOPAY_FLOW: {
      const { value } = payload;
      draft.isInAutopayFlow = value;
      break;
    }
    case SET_IS_MODAL_OPEN: {
      const { key, value } = payload;
      draft.isModalOpen[key] = value;
      break;
    }
    case STOP: {
      draft.cancelConfirmation = {
        amount: draft.statusData.currentSetting.totalMonthlyPayment,
        date: draft.statusData.currentSetting.cancellableDate,
      };
      draft.confirmationType = 'stop';
      draft.errors.serviceError.stop = false;
      draft.status.stop = 'loading';
      break;
    }
    case STOP_FAILURE: {
      draft.errors.serviceError.stop = true;
      draft.status.stop = 'failed';
      // for desktop
      draft.step = 1;
      draft.desktopView = 'stop';
      break;
    }
    case STOP_SUCCESS: {
      const { confirmationNumber } = payload;
      draft.cancelConfirmation.confirmationNumber = confirmationNumber;
      draft.status.fetchStatus = 'idle';
      draft.status.stop = 'succeeded';
      // for desktop
      draft.confirmationNumber = confirmationNumber;
      draft.step = 1;
      draft.desktopView = 'stop';
      break;
    }
    case UPDATE_AMOUNT: {
      const { amount } = payload;
      draft.form.amount = amount;
      if (draft.formErrors.amount.additionalPrincipal) {
        draft.formErrors.amount.additionalPrincipal =
          initialState.formErrors.amount.additionalPrincipal;
      }
      break;
    }
    case UPDATE_EMAIL_ALERT_SETTING_FAILURE: {
      draft.errors.serviceError.updateEmailAlerts = true;
      break;
    }
    case UPDATE_FORM_ERROR: {
      const { field, form, value } = payload;
      draft.formErrors[form][field] = value;
      break;
    }
  }
}, initialState);

export default autopayReducer;
